import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createRecipe,
  updateRecipe,
  getRecipeById,
  uploadRecipePhoto,
  scanRecipe,
  CATEGORIES,
} from '../../services/recipeService';
import { getMyGroup } from '../../services/groupService';
import { getRecipeNotesGuidance, updateRecipeNotesGuidance } from '../../services/recipeNotesGuidanceService';
import { useAuth } from '../../context/AuthContext';
import CameraCapture from './CameraCapture';

const emptyIngredient = () => ({ name: '', amount: '', unit: '', notes: '' });
const emptyStep = () => ({ instruction: '' });

// Mirrors the backend's Subscription.isActive -- active plan, OR an
// unexpired trial. Kept in one place so the add-flow guard and the
// edit-flow warning below can't drift out of sync with each other.
const isSubscriptionActive = (subscription) =>
  subscription?.status === 'active' ||
  (subscription?.status === 'trial' && subscription?.trial_ends_at && new Date(subscription.trial_ends_at) > new Date());

const RecipeForm = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState(['main']);
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');
  const [notes, setNotes] = useState('');
  const [ingredients, setIngredients] = useState([emptyIngredient()]);
  const [steps, setSteps] = useState([emptyStep()]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Scan-to-fill state (only relevant when creating a new recipe)
  const [scanInfo, setScanInfo] = useState(null); // { used, limit } | null
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [wasScanned, setWasScanned] = useState(false);
  const [accessInactive, setAccessInactive] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');

  const { user } = useAuth();
  const [notesGuidance, setNotesGuidance] = useState('');
  const [editingGuidance, setEditingGuidance] = useState(false);
  const [guidanceDraft, setGuidanceDraft] = useState('');
  const [savingGuidance, setSavingGuidance] = useState(false);

  const toggleCategory = (value) => {
    setCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const applyDraft = (draft) => {
    setTitle(draft.title || '');
    setCategories(draft.category ? [draft.category] : ['main']);
    setPrepTime(draft.prep_time ?? '');
    setCookTime(draft.cook_time ?? '');
    setServings(draft.servings ?? '');
    setIngredients(
      draft.ingredients?.length > 0
        ? draft.ingredients.map((ing) => ({
            name: ing.name || '',
            amount: ing.amount || '',
            unit: ing.unit || '',
            notes: ing.notes || '',
          }))
        : [emptyIngredient()]
    );
    setSteps(
      draft.instructions?.length > 0
        ? draft.instructions.map((s) => ({ instruction: s.instruction || '' }))
        : [emptyStep()]
    );
    setWasScanned(true);
  };

  useEffect(() => {
    const loadGuidance = async () => {
      try {
        const data = await getRecipeNotesGuidance();
        setNotesGuidance(data.guidanceText || '');
      } catch (err) {
        // non-critical -- the notes field still works fine without it
      }
    };
    loadGuidance();
  }, []);

  const startEditGuidance = () => {
    setGuidanceDraft(notesGuidance);
    setEditingGuidance(true);
  };

  const handleSaveGuidance = async () => {
    setSavingGuidance(true);
    try {
      await updateRecipeNotesGuidance(guidanceDraft);
      setNotesGuidance(guidanceDraft);
      setEditingGuidance(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save');
    } finally {
      setSavingGuidance(false);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      // Guard against navigating straight to /recipes/new without an active plan
      const checkSubscription = async () => {
        try {
          const data = await getMyGroup();
          if (!isSubscriptionActive(data.subscription)) {
            navigate('/dashboard');
            return;
          }
          setScanInfo({
            used: data.subscription.scan_count || 0,
            limit: data.subscription.scan_limit ?? 10,
          });
        } catch (err) {
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      };
      checkSubscription();
      return;
    }

    // Editing: don't redirect away (they may just want to view what's there),
    // but check up front so a warning shows immediately -- before they type
    // any changes -- rather than only failing once they hit Save.
    const checkEditAccess = async () => {
      try {
        const data = await getMyGroup();
        if (!isSubscriptionActive(data.subscription)) {
          setAccessInactive(true);
          setAccessMessage(
            data.subscription?.status === 'trial'
              ? 'Your free trial has ended. You can view this recipe, but saving changes requires purchasing a plan first.'
              : 'Purchase a plan to save changes to recipes.'
          );
        }
      } catch (err) {
        // non-critical -- if this check fails, saving will still be correctly
        // blocked server-side; this is purely an early warning
      }
    };
    checkEditAccess();

    const load = async () => {
      try {
        const data = await getRecipeById(id);
        const r = data.recipe;
        setTitle(r.title);
        setCategories(r.categories?.length > 0 ? r.categories : []);
        setPrepTime(r.prep_time || '');
        setCookTime(r.cook_time || '');
        setServings(r.servings || '');
        setNotes(r.notes || '');
        setIngredients(r.ingredients.length > 0 ? r.ingredients : [emptyIngredient()]);
        setSteps(
          r.instructions.length > 0
            ? r.instructions.map((s) => ({ instruction: s.instruction }))
            : [emptyStep()]
        );
        if (r.photo_url) setPhotoPreview(r.photo_url);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load recipe');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEditing]);

  const submitScan = async (file) => {
    setScanning(true);
    setScanError('');
    try {
      const data = await scanRecipe(file);
      setScanInfo({ used: data.scanCount, limit: data.scanLimit });
      applyDraft(data.draft);
    } catch (err) {
      if (err.response?.data?.code === 'SCAN_LIMIT_REACHED') {
        setScanInfo({ used: err.response.data.scanCount, limit: err.response.data.scanLimit });
      }
      setScanError(err.response?.data?.message || 'Failed to scan recipe');
    } finally {
      setScanning(false);
    }
  };

  const handleScanFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await submitScan(file);
    e.target.value = ''; // allow re-selecting the same file later
  };

  const handleCameraCapture = async (file) => {
    setShowCamera(false);
    await submitScan(file);
  };

  const updateIngredient = (index, field, value) => {
    setIngredients((prev) => prev.map((ing, i) => (i === index ? { ...ing, [field]: value } : ing)));
  };
  const addIngredient = () => setIngredients((prev) => [...prev, emptyIngredient()]);
  const removeIngredient = (index) => setIngredients((prev) => prev.filter((_, i) => i !== index));

  const updateStep = (index, value) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { instruction: value } : s)));
  };
  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (index) => setSteps((prev) => prev.filter((_, i) => i !== index));

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Recipe title is required');
      return;
    }

    const cleanIngredients = ingredients
      .filter((ing) => ing.name.trim())
      .map((ing, index) => ({ ...ing, sort_order: index }));
    const cleanSteps = steps
      .filter((s) => s.instruction.trim())
      .map((s, index) => ({ instruction: s.instruction, step_number: index + 1 }));

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        categories,
        prep_time: prepTime ? Number(prepTime) : null,
        cook_time: cookTime ? Number(cookTime) : null,
        servings: servings ? Number(servings) : null,
        notes: notes.trim() || null,
        ingredients: cleanIngredients,
        instructions: cleanSteps,
      };

      let recipeId = id;
      if (isEditing) {
        await updateRecipe(id, payload);
      } else {
        const created = await createRecipe(payload);
        recipeId = created.recipe.id;
      }

      if (photoFile) {
        await uploadRecipePhoto(recipeId, photoFile);
      }

      navigate(`/recipes/${recipeId}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={styles.status}>Loading recipe...</p>;

  const scanLimitReached = scanInfo && scanInfo.used >= scanInfo.limit;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>{isEditing ? 'Edit Recipe' : 'Add a Recipe'}</h2>

      {accessInactive && (
        <div style={styles.accessWarning}>⚠️ {accessMessage}</div>
      )}

      {!isEditing && (
        <div style={styles.scanSection}>
          <p style={styles.scanIntro}>Start from a photo, or fill in the form below yourself.</p>
          {scanError && <p style={styles.scanError}>{scanError}</p>}
          {scanLimitReached ? (
            <span style={styles.scanLimitBadge}>
              📷 Scan limit reached ({scanInfo.used}/{scanInfo.limit}) — buy more scans from your dashboard
            </span>
          ) : (
            <div style={styles.scanButtonRow}>
              <button type="button" onClick={() => setShowCamera(true)} style={styles.scanButton} disabled={scanning}>
                {scanning ? 'Scanning...' : `📷 Take Photo of Recipe${scanInfo ? ` (${scanInfo.limit - scanInfo.used} left)` : ''}`}
              </button>
              <label style={styles.uploadScanButton}>
                🖼️ Upload Photo of Recipe
                <input type="file" accept="image/*" onChange={handleScanFile} style={{ display: 'none' }} disabled={scanning} />
              </label>
            </div>
          )}
        </div>
      )}

      {showCamera && (
        <CameraCapture onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />
      )}

      {!isEditing && wasScanned && (
        <p style={styles.scanNotice}>
          📷 Filled in from your scanned photo — double-check everything below before saving, AI extraction isn't always perfect.
        </p>
      )}

      <form onSubmit={handleSubmit}>
        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.section}>
          <label style={styles.label}>Photo</label>
          <div style={styles.photoRow}>
            {photoPreview && <img src={photoPreview} alt="preview" style={styles.photoPreview} />}
            <input type="file" accept="image/*" onChange={handlePhotoChange} />
          </div>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={styles.input}
            placeholder="e.g. Grandma's Sunday Sauce"
          />
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Categories <span style={styles.optionalTag}>(select all that apply)</span></label>
          <div style={styles.categoryPillRow}>
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => toggleCategory(c.value)}
                style={categories.includes(c.value) ? styles.categoryPillActive : styles.categoryPill}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.row}>
          <div style={styles.section}>
            <label style={styles.label}>Prep (min)</label>
            <input type="number" min="0" value={prepTime} onChange={(e) => setPrepTime(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.section}>
            <label style={styles.label}>Cook (min)</label>
            <input type="number" min="0" value={cookTime} onChange={(e) => setCookTime(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.section}>
            <label style={styles.label}>Servings</label>
            <input type="number" min="0" value={servings} onChange={(e) => setServings(e.target.value)} style={styles.input} />
          </div>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Ingredients</label>
          {ingredients.map((ing, index) => (
            <div key={index} style={styles.ingredientRow}>
              <input
                placeholder="Amount"
                value={ing.amount}
                onChange={(e) => updateIngredient(index, 'amount', e.target.value)}
                style={styles.ingAmount}
              />
              <input
                placeholder="Unit"
                value={ing.unit}
                onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                style={styles.ingUnit}
              />
              <input
                placeholder="Ingredient name"
                value={ing.name}
                onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                style={styles.ingName}
              />
              <button type="button" onClick={() => removeIngredient(index)} style={styles.removeRowBtn}>✕</button>
            </div>
          ))}
          <button type="button" onClick={addIngredient} style={styles.addRowBtn}>+ Add Ingredient</button>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Instructions</label>
          {steps.map((step, index) => (
            <div key={index} style={styles.stepRow}>
              <span style={styles.stepNumber}>{index + 1}</span>
              <textarea
                value={step.instruction}
                onChange={(e) => updateStep(index, e.target.value)}
                style={styles.stepInput}
                rows={2}
                placeholder={`Step ${index + 1}...`}
              />
              <button type="button" onClick={() => removeStep(index)} style={styles.removeRowBtn}>✕</button>
            </div>
          ))}
          <button type="button" onClick={addStep} style={styles.addRowBtn}>+ Add Step</button>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Notes <span style={styles.optionalTag}>(optional)</span></label>

          {editingGuidance ? (
            <div style={styles.guidanceEditBox}>
              <textarea
                value={guidanceDraft}
                onChange={(e) => setGuidanceDraft(e.target.value)}
                style={styles.guidanceTextarea}
                rows={3}
              />
              <div style={styles.guidanceEditActions}>
                <button type="button" onClick={() => setEditingGuidance(false)} style={styles.guidanceCancelBtn} disabled={savingGuidance}>
                  Cancel
                </button>
                <button type="button" onClick={handleSaveGuidance} style={styles.guidanceSaveBtn} disabled={savingGuidance}>
                  {savingGuidance ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            (notesGuidance || user?.is_admin) && (
              <div style={styles.guidanceBox}>
                <p style={styles.guidanceText}>
                  {notesGuidance || (user?.is_admin ? 'No guidance text set yet — click Edit to add some.' : '')}
                </p>
                {user?.is_admin && (
                  <button type="button" onClick={startEditGuidance} style={styles.guidanceEditBtn}>Edit</button>
                )}
              </div>
            )
          )}

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={styles.notesInput}
            rows={5}
            placeholder="Tips, substitutions, family history, anything worth remembering about this recipe..."
          />
        </div>

        <div style={styles.actions}>
          <button type="button" onClick={() => navigate(-1)} style={styles.cancelBtn}>Cancel</button>
          <button type="submit" style={styles.saveBtn} disabled={saving || accessInactive}>
            {saving ? 'Saving...' : accessInactive ? 'Purchase a Plan to Save' : isEditing ? 'Save Changes' : 'Create Recipe'}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  title: { color: '#e67e22', marginBottom: '24px' },
  scanSection: {
    backgroundColor: '#fafafa',
    border: '1px solid #eee',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '24px',
  },
  scanIntro: { color: '#888', fontSize: '13px', marginBottom: '10px' },
  scanButtonRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  scanButton: {
    padding: '10px 20px',
    backgroundColor: 'white',
    color: '#e67e22',
    border: '1px solid #e67e22',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  uploadScanButton: {
    padding: '10px 20px',
    backgroundColor: 'white',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-block',
  },
  scanLimitBadge: {
    display: 'inline-block',
    padding: '10px 16px',
    backgroundColor: '#f5f5f5',
    color: '#999',
    border: '1px solid #eee',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
  },
  scanError: { color: '#e74c3c', fontSize: '13px', marginBottom: '10px' },
  scanNotice: {
    backgroundColor: '#fdf0e3',
    color: '#a05a1a',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  section: { marginBottom: '20px', flex: 1 },
  row: { display: 'flex', gap: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#444', fontSize: '14px' },
  optionalTag: { fontWeight: '400', color: '#aaa', fontSize: '12px' },
  categoryPillRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  categoryPill: {
    padding: '8px 16px', backgroundColor: 'white', color: '#666', border: '1px solid #ddd',
    borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
  },
  categoryPillActive: {
    padding: '8px 16px', backgroundColor: '#e67e22', color: 'white', border: '1px solid #e67e22',
    borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  photoRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  photoPreview: { width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' },
  ingredientRow: { display: 'flex', gap: '8px', marginBottom: '8px' },
  ingAmount: { width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' },
  ingUnit: { width: '90px', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' },
  ingName: { flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd' },
  stepRow: { display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' },
  stepNumber: {
    width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#fdf0e3',
    color: '#e67e22', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700', flexShrink: 0, marginTop: '6px',
  },
  stepInput: { flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical' },
  notesInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  guidanceBox: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px',
    backgroundColor: '#fdf0e3', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px',
  },
  guidanceText: { color: '#a05a1a', fontSize: '12px', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' },
  guidanceEditBtn: {
    padding: '4px 10px', backgroundColor: 'white', color: '#e67e22', border: '1px solid #e67e22',
    borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', flexShrink: 0,
  },
  guidanceEditBox: { marginBottom: '10px' },
  guidanceTextarea: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
  },
  guidanceEditActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' },
  guidanceCancelBtn: {
    padding: '6px 14px', backgroundColor: 'transparent', border: '1px solid #ddd',
    borderRadius: '6px', cursor: 'pointer', color: '#666', fontSize: '12px',
  },
  guidanceSaveBtn: {
    padding: '6px 16px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '12px',
  },
  removeRowBtn: {
    background: 'transparent', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '14px',
  },
  addRowBtn: {
    background: 'transparent', border: '1px dashed #e67e22', color: '#e67e22',
    padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', marginTop: '4px',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '28px' },
  cancelBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ddd',
    borderRadius: '8px', cursor: 'pointer', color: '#666',
  },
  saveBtn: {
    padding: '10px 24px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
  },
  error: { color: '#e74c3c', marginBottom: '16px' },
  accessWarning: {
    backgroundColor: '#fdf0e3', border: '1px solid #f0c896', color: '#a05a1a',
    padding: '14px 18px', borderRadius: '10px', marginBottom: '20px', fontSize: '13px', lineHeight: 1.5,
  },
  status: { textAlign: 'center', color: '#999' },
};

export default RecipeForm;
